import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import Select from '@/components/ui/Select';
import { Button, Divider, Input } from 'antd';
import FormItem from '@/components/ui/FormItem';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useCreateWorkstation } from '@/hooks/v1/useWorkstationMutations';
import { useWorkstation } from '@/components/views/Report/KimLhc/Components/Form/hooks';

interface WorkstationFormItemProps {
	fieldName?: string | string[];
	required?: boolean;
}

export function WorkstationFormItem({ required = true, fieldName }: Readonly<WorkstationFormItemProps>): JSX.Element {
	const [workstationName, setWorkstationName] = useState<string>('');

	const { organization, company } = useApplicationContext();
	const { isLoading: isCreateWorkstationLoading, mutateAsync: createWorkstation } = useCreateWorkstation();

	const { items, isFetching, isDisabled, workstationId, lineId, handleClearWorkstation, handleSelectWorkstation } =
		useWorkstation({ fieldName });

	async function handleCreateWorkstation(): Promise<void> {
		const name = workstationName.trim();

		if (!name || !lineId) return;

		const body = {
			organization_id: organization.id!,
			company_id: company.id!,
			line_id: lineId,
			name
		};

		const result = await createWorkstation({ body });

		const newId = result?.data?.id;

		if (newId) {
			handleSelectWorkstation(newId);
		}

		setWorkstationName('');
	}

	return (
		<FormItem
			name={fieldName}
			required={required}
			label="Workstation"
			requiredMessage="The field requires an answer"
		>
			<Select
				allowClear
				showSearch
				options={items}
				loading={isFetching}
				value={workstationId}
				disabled={isDisabled}
				onClear={handleClearWorkstation}
				onChange={handleSelectWorkstation}
				placeholder="Workstation"
				dropdownRender={(menu) => (
					<>
						{menu}
						<Divider style={{ margin: '8px 0' }} />
						<div style={{ padding: '0 8px 4px', display: 'flex', gap: 8 }}>
							<Input
								value={workstationName}
								onChange={(e) => setWorkstationName(e.target.value)}
								placeholder={I18n.get('New workstation name')}
								style={{ flex: 1 }}
							/>
							<Button
								type="text"
								onClick={handleCreateWorkstation}
								loading={isCreateWorkstationLoading}
								disabled={!lineId || !workstationName.trim() || workstationName.trim().length < 2}
							>
								{I18n.get('Add')}
							</Button>
						</div>
					</>
				)}
			/>
		</FormItem>
	);
}
